// First, before core-js: what the browser has without it (the PDF preview's worker gets only that)
import '@helpers/nativeBuiltIns';
import 'core-js';
import 'regenerator-runtime';
import React from 'react';
import ReactDOM from 'react-dom';
import { cold, setConfig, AppContainer } from 'react-hot-loader';
import AppEntryPoint from './app';
import Bugsnag from '@bugsnag/js';
import { get } from 'lodash';
import { getPlatform } from '@helpers/helpers';
import { scrubBreadcrumb, scrubEditorEvent } from '@helpers/bugsnagPrivacy';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { isUnhandledApiFailure } from '@helpers/Api';
import { watchForBlankPage, retryFailedCordovaStart, cordovaRestart } from '@helpers/appRecovery';
import { handleUnhandledQuotaError } from '@helpers/helpers_index/browser';

/**
 * Setup HMR settings
 */
if (RUNNING_ON_DEV_SERVER && module.hot) {
  setConfig({
    ignoreSFC: false,
    pureRender: false,
    pureSFC: true,
    reloadHooks: true,
    onComponentCreate: (type) => (String(type).indexOf('useState') > 0 || String(type).indexOf('useEffect') > 0) && cold(type),
  });
}
/**
 * Initialize bugsnag client in entry of app avoiding invalid instance problems
 */
if (window.ENV === 'production') {
  Bugsnag.start({
    apiKey: BUGSNAG_API_KEY,
    appVersion: PACKAGE_APP_VERSION,
    plugins: [new BugsnagPluginReact()],
    appType: getPlatform(),
    releaseStage: window.ENV,
    onBreadcrumb: scrubBreadcrumb,
    onError: (event) => {
      // A full disk: the image caches cleared and StorageFull once, instead of a QuotaExceededError of its own
      if (handleUnhandledQuotaError(event)) return false;
      if (isUnhandledApiFailure(event)) return false;
      // The record editor's errors may quote the record: their class and frames only
      scrubEditorEvent(event);

      event.setUser(get(window, ['debugInfo', 'userId'], null));

      event.addMetadata('clinic', {
        clinicId: get(window, ['debugInfo', 'clinicId'], null),
      });
      return true;
    },

  });

  // Bugsnag sends at most 10 events per page, and only sees a new one on history.pushState. Our
  // routes change the hash instead, so the 10 were all a whole app session ever got.
  window.addEventListener('hashchange', () => Bugsnag.resetEventCount());
}

// Setup Simple global's
window.APP_VERSION = PACKAGE_APP_VERSION;
window.APP_LS_KEY = `doutore-${window.btoa(window.location.hostname)}`;
window.hashUid = () => 'initial_hash_uid' + Math.random();

if (localStorage.getItem('force-cordova') === 'true') {
  window.isCordovaApp = true;
  window.isCordovaIos = localStorage.getItem('force-ios') === 'true';
}



const startRender = (COMPONENT: React.FunctionComponent | React.ElementType) => (RUNNING_ON_DEV_SERVER && module.hot ? (
  <AppContainer>
    <COMPONENT />
  </AppContainer>
) : (
  <COMPONENT />
));

// A white page with no way out but force-quitting the app (the native app back from Safari, 24/10): a
// "Recarregar" screen instead, whatever left the page blank.
watchForBlankPage(document.getElementById('app'), (reason) => {
  if (window.ENV !== 'production') return;
  const error = new Error(`The app's page stayed blank (${reason})`);
  error.name = 'BlankPage';
  // A start that failed after the native apps had already started over on their own (below): what made them
  const restart = reason === 'never_drew' ? cordovaRestart() : null;
  Bugsnag.notify(error, (event) => {
    if (restart) event.addMetadata('cordovaRestart', restart);
  });
});

// Before the "Recarregar" screen, the native apps start over once by themselves when cordova's plugin loader fails (a
// plugin's script lost at launch, iPhone 4.16.49), and tell Bugsnag once that start works
retryFailedCordovaStart((restart) => {
  if (window.ENV !== 'production') return;
  const error = new Error(`The app started over (${restart.reason})`);
  error.name = 'CordovaRestarted';
  Bugsnag.notify(error, (event) => {
    event.severity = 'info';
    event.addMetadata('cordovaRestart', restart);
  });
});

if (window.cordova) {
  document.addEventListener('deviceready', () => {
    ReactDOM.render(startRender(AppEntryPoint), document.getElementById('app'));
  });
} else {
  ReactDOM.render(startRender(AppEntryPoint), document.getElementById('app'));
}


if (RUNNING_ON_DEV_SERVER && module.hot) {
  module.hot.accept('./app.js', () => {
    const newEntryPoint = require('./app').default;
    ReactDOM.render(startRender(newEntryPoint), document.getElementById('root'));
  });
}
